import { Component, Input, OnInit } from '@angular/core';
import { EbwModalService } from '@ebw-monorepo/modal';
import { lastValueFrom, switchMap, take, takeWhile, timer } from 'rxjs';
import { AnteaService } from '../../../../services/antea.service';
import { AnteaElaborazione, ControlloDaTrasmettere, EsitoTrasmissioneSuperamento, SuperamentoDaTrasmettere } from '../../../../models/antea.model';

type Riga = (ControlloDaTrasmettere | SuperamentoDaTrasmettere) & { selected: boolean; codicePrelievo?: string; codiceParametro?: string };

const isStatoTerminale = (stato: string): boolean => /CONCLUSA|ERRORE/i.test(stato);

const MAX_TENTATIVI_POLLING = 60;

const ETICHETTE_STATO: Record<string, string> = {
  IN_CODA: 'In coda',
  ELABORAZIONE_INSERITA_IN_CODA: 'In coda',
  SALVATO_SU_DATALAKE: 'Salvato, in elaborazione',
  ELABORAZIONE_CONCLUSA: 'Completata con successo',
  ERRORE: 'Errore'
};

function etichettaLeggibile(codice: string): string {
  return ETICHETTE_STATO[codice] ?? codice.replace(/_/g, ' ').toLowerCase();
}

@Component({
  selector: 'ebw-monorepo-trasmissione-antea-sezione',
  templateUrl: './trasmissione-antea-sezione.component.html',
  styleUrls: ['./trasmissione-antea-sezione.component.scss']
})
export class TrasmissioneAnteaSezioneComponent implements OnInit {
  @Input() tipo: 'prelievi' | 'superamenti';

  public columns: string[];
  public wszOptions: string[] = [];
  public selectedWsz: string | null = null;
  public rows: Riga[] = [];
  public loadingWsz = false;
  public loadingRows = false;
  public trasmitting = false;
  public elaborazione: AnteaElaborazione | null = null;
  public risultatiSuperamenti: EsitoTrasmissioneSuperamento[] = [];

  private _righeTrasmesse: Riga[] = [];

  constructor(
    private _anteaService: AnteaService,
    private _modalService: EbwModalService
  ) {}

  ngOnInit(): void {
    this.columns =
      this.tipo === 'prelievi'
        ? ['selected', 'idPuntoCampionamento', 'dataOraPrelievo', 'codicePrelievo']
        : ['selected', 'idPuntoCampionamento', 'dataOraPrelievo', 'codiceParametro'];

    this.loadWsz();
  }

  public get selectedCount(): number {
    return this.rows.filter((r) => r.selected).length;
  }

  public get statoLabel(): string {
    return this.elaborazione ? etichettaLeggibile(this.elaborazione.statoElaborazione) : '';
  }

  public get erroreLabel(): string | null {
    const errore = this.elaborazione?.erroreElaborazione;
    return typeof errore === 'string' && errore.length ? etichettaLeggibile(errore) : null;
  }

  public datiRiga(id: number): Riga | undefined {
    return this._righeTrasmesse.find((r) => r.id === id);
  }

  public async loadWsz(): Promise<void> {
    this.loadingWsz = true;
    try {
      this.wszOptions = await lastValueFrom(this._anteaService.getWsz());
    } catch (error: any) {
      this._modalService.openErrorModal({
        title: 'Errore',
        content: error?.error?.message ?? 'Errore durante il recupero delle WSZ'
      });
    } finally {
      this.loadingWsz = false;
    }
  }

  public async onWszChange(resetElaborazione = true): Promise<void> {
    if (!this.selectedWsz) {
      return;
    }

    this.loadingRows = true;
    this.rows = [];
    if (resetElaborazione) {
      this.elaborazione = null;
      this.risultatiSuperamenti = [];
    }

    try {
      if (this.tipo === 'prelievi') {
        const controlli = await lastValueFrom(this._anteaService.getControlliDaTrasmettere(this.selectedWsz));
        this.rows = controlli.map((c) => ({ ...c, selected: true }));
      } else {
        const superamenti = await lastValueFrom(this._anteaService.getSuperamentiDaTrasmettere(this.selectedWsz));
        this.rows = superamenti.map((s) => ({ ...s, selected: true }));
      }
    } catch (error: any) {
      this._modalService.openErrorModal({
        title: 'Errore',
        content: error?.error?.message ?? 'Errore durante il recupero dei dati da trasmettere'
      });
    } finally {
      this.loadingRows = false;
    }
  }

  public async trasmetti(): Promise<void> {
    const righeSelezionate = this.rows.filter((r) => r.selected);
    const ids = righeSelezionate.map((r) => r.id);
    if (!ids.length) {
      return;
    }

    this.trasmitting = true;
    try {
      if (this.tipo === 'prelievi') {
        this.elaborazione = await lastValueFrom(this._anteaService.trasmettiControlli(ids));
        this.rows = this.rows.filter((r) => !ids.includes(r.id));
        this._pollElaborazione(this.elaborazione.idElaborazione);
      } else {
        this._righeTrasmesse = righeSelezionate;
        this.risultatiSuperamenti = await lastValueFrom(this._anteaService.trasmettiSuperamenti(ids));
        const idsSuccesso = this.risultatiSuperamenti.filter((r) => r.successo).map((r) => r.id);
        this.rows = this.rows.filter((r) => !idsSuccesso.includes(r.id));
      }
    } catch (error: any) {
      this._modalService.openErrorModal({
        title: 'Errore',
        content: error?.error?.message ?? 'Errore durante la trasmissione'
      });
    } finally {
      this.trasmitting = false;
    }
  }

  private _pollElaborazione(idElaborazione: string): void {
    timer(5000, 5000)
      .pipe(
        take(MAX_TENTATIVI_POLLING),
        switchMap(() => this._anteaService.getElaborazione(idElaborazione)),
        takeWhile((e) => !isStatoTerminale(e.statoElaborazione), true)
      )
      .subscribe({
        next: (elaborazione) => {
          this.elaborazione = elaborazione;
          if (isStatoTerminale(elaborazione.statoElaborazione)) {
            this.onWszChange(false);
          }
        },
        error: (error: any) => {
          this._modalService.openErrorModal({
            title: 'Errore',
            content: error?.error?.message ?? 'Errore durante il controllo dello stato elaborazione'
          });
        }
      });
  }
}
